import React, { FC, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/system";
import { Fade } from "@mui/material";
import i18next from "i18next";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { ApplicationFeature } from "@green0meter/api-client";

import { Filter } from "./PbiReport.types";
import { generateFilters, getWindowSize } from "./PbiReport.helpers";
import { PbiReportContainer } from "./PbiReport.styled";

import { useUserStore } from "@store/user";
import AnimatedLoader from "@components/common/AnimatedLoader";
import { REPORT_ID } from "@constants";
import { useGetCurrentCompanyInfo, useGetReport } from "@api";

interface PbiReport {
  reportId?: string;
  pageId: string;
  extraFilters?: Filter[];
  height: number;
  bg: "default" | "transparent";
  reportType?: ApplicationFeature;
}

const PbiReport: FC<PbiReport> = ({
  reportId = REPORT_ID,
  pageId,
  extraFilters = [],
  height,
  bg,
  reportType,
}) => {
  const { user } = useUserStore();
  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);

  const [embedConfig, setEmbedConfig] = useState({
    embedUrl: "",
    accessToken: "",
  });
  const [loaded, setLoaded] = useState(false);
  const [filtersConfig, setFiltersConfig] = useState(
    generateFilters(i18next.resolvedLanguage || "en"),
  );
  const [windowSize, setWindowSize] = useState(getWindowSize());

  // For CO2 we use demodata in relation to flag isDemoDataEnabled, either redo this logic - add other custom flags
  // or chnage this logic
  const isDemoCO2 =
    reportType === ApplicationFeature.CO2 && currentCompanyInfo?.isDemoDataEnabled;

  const clientId = useMemo(
    () => (isDemoCO2 ? "1" : (user?.activeCompanyId ?? "1")),
    [isDemoCO2, user?.activeCompanyId],
  );

  const { report, isSuccessGetReport } = useGetReport(user, reportId, "", clientId);

  useEffect(() => {
    setLoaded(false);
    setEmbedConfig({ embedUrl: "", accessToken: "" });
  }, [clientId]);

  useEffect(() => {
    i18next.on("languageChanged", (lang) => {
      setFiltersConfig(generateFilters(lang));
    });
  }, []);

  useEffect(() => {
    const handleWindowResize = (): void => {
      setWindowSize(getWindowSize());
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (report && isSuccessGetReport) {
      setEmbedConfig({
        embedUrl: report.embedUrl,
        accessToken: report.accessToken,
      });
    }
  }, [report, isSuccessGetReport]);

  const powerBiKey = `${reportId}-${clientId}-${pageId}-${bg}-${i18next.resolvedLanguage}`;

  return (
    <Box
      sx={{
        height: windowSize.innerWidth < 500 ? "auto" : height,
        position: "relative",
      }}
    >
      <Fade in={!loaded} timeout={500} unmountOnExit>
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1,
            background: "#ffffff",
          }}
        >
          <AnimatedLoader />
        </Box>
      </Fade>
      <PbiReportContainer>
        {embedConfig.accessToken && embedConfig.embedUrl && (
          <PowerBIEmbed
            key={powerBiKey}
            embedConfig={{
              filters: [...filtersConfig, ...extraFilters],
              type: "report",
              id: reportId,
              embedUrl: embedConfig?.embedUrl,
              accessToken: embedConfig?.accessToken,
              tokenType: models.TokenType.Embed,
              settings: {
                layoutType:
                  windowSize.innerWidth < 500
                    ? models.LayoutType.MobilePortrait
                    : models.LayoutType.Master,
                filterPaneEnabled: false,
                navContentPaneEnabled: false,
                panes: {
                  filters: {
                    visible: false,
                  },
                  pageNavigation: {
                    visible: false,
                  },
                },
                background:
                  bg === "default"
                    ? models.BackgroundType.Default
                    : models.BackgroundType.Transparent,
              },
              pageName: pageId,
            }}
            cssClassName={"report-style-class"}
            eventHandlers={
              new Map([
                [
                  "rendered",
                  (): void => {
                    setLoaded(true);
                  },
                ],
              ])
            }
          />
        )}
      </PbiReportContainer>
    </Box>
  );
};

export default PbiReport;
